/**
 * Hand-rolled shadcn-style Button.
 *
 * Variants:
 * - primary: filled brand-blue button (sign-in, retry).
 * - secondary: outlined ink-700 (subtle, used for log-out).
 * - ghost: chromeless (e.g., the tooltip "?" trigger).
 *
 * Carried forward verbatim from packages/spa/src/components/ui/Button.tsx.
 */
import { type ButtonHTMLAttributes, type ReactNode } from 'react';

function cn(...classes: (string | false | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 focus-visible:bg-brand-700 disabled:bg-brand-100 disabled:text-ink-500',
  secondary:
    'bg-white text-ink-700 border border-ink-200 hover:border-brand-500 hover:text-brand-700',
  ghost: 'bg-transparent text-ink-500 hover:text-brand-600 focus-visible:text-brand-700',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1 text-xs font-medium',
  md: 'px-4 py-2 text-sm font-medium',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
